'use client';

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAuth } from '@/lib/auth/AuthProvider';
import { createReview, checkHasReviewed } from '@/lib/api/purchaser/api';
import { PostReviewDialog } from '@/features/view-detail/components/PostReviewDialog';

type ReviewDialogTriggerProps = {
  providerId: number;
  providerName: string;
  providerImage?: string | null;
};

export function ReviewDialogTrigger({
  providerId,
  providerName,
  providerImage
}: ReviewDialogTriggerProps) {
  const searchParams = useSearchParams();
  const { status } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status !== 'authenticated' || searchParams.get('action') !== 'review')
      return;
    checkHasReviewed(providerId)
      .then((result) => {
        if (!result?.hasReviewed) {
          setIsOpen(true);
        }
      })
      .catch(() => {});
  }, [status, providerId, searchParams]);

  const handleSubmit = useCallback(
    async (data: {
      title?: string;
      text: string;
      stars: number;
      isReferenceAvailable: boolean;
    }) => {
      setIsSubmitting(true);
      setError(null);
      try {
        await createReview({
          providerId,
          title: data.title,
          text: data.text,
          stars: data.stars,
          isReferenceAvailable: data.isReferenceAvailable
        });
        setIsOpen(false);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to submit review';
        setError(message);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [providerId]
  );

  return (
    <PostReviewDialog
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      onSubmit={handleSubmit}
      providerName={providerName}
      providerImage={providerImage}
      loading={isSubmitting}
      error={error}
    />
  );
}
